import React, { useState } from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import { clientsAPI } from '../../../api';
import { inputStyle, labelStyle, initials, fmtMonthYear } from '../utils';

const contactRow = {
  display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: '#4a463e',
  textDecoration: 'none', lineHeight: 1.4,
};

export const ClientInfoCard = ({ client, t, lang, onUpdated, onDeleted, showToast }) => {
  const [editing, setEditing] = useState(false);
  const [editingInfo, setEditingInfo] = useState(false);
  const [form, setForm] = useState({});
  const [info, setInfo] = useState(client.important_info || '');

  const startEdit = () => {
    setForm({
      name: client.name || '', email: client.email || '', phone: client.phone || '',
      address: client.address || '', company: client.company || '',
    });
    setEditing(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    try {
      const { data } = await clientsAPI.update(client.id, form);
      onUpdated(data);
      setEditing(false);
      showToast(t.cs_saved || 'Saved');
    } catch (e) { /* noop */ }
  };

  const saveInfo = async () => {
    setEditingInfo(false);
    if (info === (client.important_info || '')) return;
    try {
      const { data } = await clientsAPI.update(client.id, { important_info: info });
      onUpdated(data);
      showToast(t.cs_saved || 'Saved');
    } catch (e) {
      setInfo(client.important_info || '');
      showToast(t.save_failed || 'Could not save');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t.cs_delete_client_confirm || 'Delete this client and their entire record? Boards stay untouched.')) return;
    try {
      await clientsAPI.remove(client.id);
      onDeleted();
    } catch (e) { /* noop */ }
  };

  const mapsUrl = client.address ? `https://maps.google.com/?q=${encodeURIComponent(client.address)}` : null;

  return (
    <section data-testid="client-info-card">
      {!editing ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 40, flexWrap: 'wrap' }}>
          {/* Left: monogram + identity */}
          <div style={{ display: 'flex', gap: 22, alignItems: 'center', minWidth: 0 }}>
            <div aria-hidden="true" style={{
              width: 74, height: 74, borderRadius: '50%', flexShrink: 0,
              background: '#EAE3D6', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Playfair Display, serif', fontSize: 24, letterSpacing: '0.06em', color: '#7d745f',
            }}>{initials(client.name)}</div>
            <div style={{ minWidth: 0 }}>
              <h1 data-testid="client-name" style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(26px, 3.4vw, 34px)', fontWeight: 400, color: 'var(--kahla-charcoal)', margin: 0, lineHeight: 1.15 }}>
                {client.name}
              </h1>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: 15, color: 'var(--kahla-text-light)', margin: '6px 0 0' }}>
                {client.company ? `${client.company} · ` : ''}{t.cs_since || 'Collaboration since'} {fmtMonthYear(client.created_at, lang)}
              </p>
            </div>
          </div>

          {/* Right: contact with icons + edit */}
          <div style={{ display: 'flex', gap: 36, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ display: 'grid', gap: 9 }}>
              {client.phone && (
                <a href={`tel:${client.phone.replace(/\s+/g, '')}`} style={contactRow} data-testid="client-action-call">
                  <Phone size={13} strokeWidth={1.4} color="#a69888" />
                  <span data-testid="client-phone" style={{ borderBottom: '1px solid var(--kahla-border)', paddingBottom: 1 }}>{client.phone}</span>
                </a>
              )}
              {client.email && (
                <a href={`mailto:${client.email}`} style={contactRow} data-testid="client-action-email">
                  <Mail size={13} strokeWidth={1.4} color="#a69888" />
                  <span data-testid="client-email" style={{ borderBottom: '1px solid var(--kahla-border)', paddingBottom: 1 }}>{client.email}</span>
                </a>
              )}
              {client.address && (
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={contactRow} data-testid="client-action-map">
                  <MapPin size={13} strokeWidth={1.4} color="#a69888" />
                  <span data-testid="client-address" style={{ borderBottom: '1px solid var(--kahla-border)', paddingBottom: 1 }}>{client.address}</span>
                </a>
              )}
            </div>
            <button type="button" className="btn-ghost" data-testid="client-edit-btn" onClick={startEdit} style={{ fontSize: 11 }}>
              {t.cs_edit || 'Edit'}
            </button>
          </div>
        </div>
      ) : (
        <div data-testid="client-edit-form" style={{ maxWidth: 640, display: 'grid', gap: 16 }}>
          <div>
            <label style={labelStyle}>{t.cs_client_name || 'Name'} *</label>
            <input data-testid="client-edit-name" style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>{t.cs_company || 'Company (optional)'}</label>
            <input data-testid="client-edit-company" style={inputStyle} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>{t.cs_email || 'Email'}</label>
              <input data-testid="client-edit-email" style={inputStyle} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>{t.cs_phone || 'Phone'}</label>
              <input data-testid="client-edit-phone" style={inputStyle} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>{t.cs_address || 'Address'}</label>
            <input data-testid="client-edit-address" style={inputStyle} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <button
              type="button" data-testid="client-delete-btn"
              onClick={handleDelete}
              style={{
                background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                fontFamily: 'Playfair Display, serif', fontSize: 10, letterSpacing: '0.18em',
                textTransform: 'uppercase', color: '#b0483a',
              }}
            >{t.cs_delete_client || 'Delete client'}</button>
            <div style={{ display: 'flex', gap: 12 }}>
              <button type="button" className="btn-ghost" onClick={() => setEditing(false)}>{t.cs_cancel || 'Cancel'}</button>
              <button type="button" className="btn-primary" data-testid="client-edit-save" onClick={save} disabled={!form.name?.trim()}>{t.cs_save || 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Important Information — calm display, click to edit */}
      <div style={{ marginTop: 34, maxWidth: 760 }} data-testid="client-important-info">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
          <label style={{ ...labelStyle, margin: 0 }}>{t.cs_important_info || 'Important information'}</label>
          {!editingInfo && (
            <button
              type="button" data-testid="client-important-info-edit"
              onClick={() => setEditingInfo(true)}
              style={{
                background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                fontFamily: 'Playfair Display, serif', fontSize: 9.5, letterSpacing: '0.16em',
                textTransform: 'uppercase', color: '#cf9a3d',
              }}
            >{(client.important_info || '').trim() ? (t.cs_edit || 'Edit') : (t.cs_add || 'Add')}</button>
          )}
        </div>
        {editingInfo ? (
          <textarea
            data-testid="client-important-info-input"
            value={info}
            onChange={(e) => setInfo(e.target.value)}
            onBlur={saveInfo}
            autoFocus
            rows={Math.min(10, Math.max(3, info.split('\n').length + 1))}
            placeholder={t.cs_important_info_hint || 'Anything worth remembering…'}
            style={{
              ...inputStyle, marginTop: 10,
              fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontSize: 16, lineHeight: 1.7,
              resize: 'vertical', background: 'rgba(255,255,255,0.6)',
            }}
          />
        ) : (client.important_info || '').trim() ? (
          <p
            data-testid="client-important-info-text"
            onClick={() => setEditingInfo(true)}
            style={{
              fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
              fontSize: 16.5, lineHeight: 1.75, color: '#4a463e', margin: '10px 0 0',
              borderLeft: '2px solid #cf9a3d', paddingLeft: 18, whiteSpace: 'pre-wrap', cursor: 'text',
            }}
          >{client.important_info}</p>
        ) : (
          <p
            onClick={() => setEditingInfo(true)}
            style={{
              fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
              fontSize: 14.5, color: 'var(--kahla-text-light)', margin: '10px 0 0', cursor: 'text',
            }}
          >{t.cs_important_info_hint || 'Anything worth remembering…'}</p>
        )}
      </div>
    </section>
  );
};

export default ClientInfoCard;
